/* Runtime configuration for the transcribe-web client.
 *
 * Two delivery modes are supported:
 *
 *   Direct mode   — the bundle holds transcribeUrl + apiKey and posts
 *                   straight to transcribe-kit. Used when the page is
 *                   embedded in a host that controls the credentials
 *                   (Delphi WebView2, an iframe parent), or in dev where
 *                   the developer keeps a local dev/config.json.
 *
 *   Proxy mode    — neither transcribeUrl nor apiKey are present. The
 *                   client posts same-origin to /api/transcribe, and the
 *                   reverse proxy in front of the static bundle adds the
 *                   X-Api-Key header. Bundle holds no secret.
 *
 * Sources, in priority order:
 *   1. window.transcribeWeb.config           (host injection, sync)
 *   2. fetch ./config.json                   (per-deployment file, async)
 *   3. {} — proxy mode with no label        (default for plain deploy)
 */
import { z } from "zod";

// One BCP-47 short code, or a comma-separated list of them ("ru,de"):
// a list is the candidate set transcribe-kit's detector chooses from.
const LANG = "[a-z]{2,3}(-[A-Z]{2})?";
const LANGUAGE_RE = new RegExp(`^${LANG}(,${LANG})*$`);

const schema = z.object({
  transcribeUrl: z.string().url().optional(),
  apiKey:        z.string().min(1).optional(),
  tenantLabel:   z.string().optional(),
  theme:         z.enum(["light", "dark", "auto"]).optional(),
  language:      z.string().regex(LANGUAGE_RE).optional(),
}).refine(
  (c) => (c.transcribeUrl == null) === (c.apiKey == null),
  { message: "transcribeUrl and apiKey must be both set or both omitted" },
);

export type VoiceConfig = z.infer<typeof schema>;

let _config: VoiceConfig | null = null;

/** Return the loaded config. Throws if loadConfig() did not run yet. */
export function getConfig(): VoiceConfig {
  if (!_config) throw new Error("config not loaded");
  return _config;
}

/** Test-only override — set the config directly, bypassing loadConfig(). */
export function setConfig(c: VoiceConfig): void {
  _config = schema.parse(c);
}

/** Resolve runtime config from one of the supported sources. Throws on
 *  schema validation failure (caller renders the ConfigError screen). */
export async function loadConfig(): Promise<VoiceConfig> {
  // 1) Host-injected config (Delphi WebView, iframe parent, ...)
  const w = window as unknown as { transcribeWeb?: { config?: unknown } };
  const embedded = w.transcribeWeb?.config;
  if (embedded !== undefined) {
    return (_config = schema.parse(embedded));
  }

  // 2) ./config.json next to index.html (deployment-time)
  try {
    const r = await fetch("./config.json", { cache: "no-store" });
    if (r.ok) {
      return (_config = schema.parse(await r.json()));
    }
    // 404 / 410 / etc. — fine, fall through to proxy-mode default
  } catch {
    // network error in dev with no middleware — fall through
  }

  // 3) Proxy mode default — same-origin /api/transcribe, no label
  return (_config = {});
}
